import { useRef, useCallback, useState } from "react";

interface UsePinchZoomOptions {
  minZoom?: number;
  maxZoom?: number;
  initialZoom?: number;
}

export function usePinchZoom({
  minZoom = 0.25,
  maxZoom = 5,
  initialZoom = 1,
}: UsePinchZoomOptions = {}) {
  const [zoom, setZoom] = useState(initialZoom);
  const lastTouchDistRef = useRef<number | null>(null);
  const lastZoomRef = useRef(initialZoom);

  const getDistance = (touches: React.TouchList) => {
    const t0 = touches[0];
    const t1 = touches[1];
    const dx = t0.clientX - t1.clientX;
    const dy = t0.clientY - t1.clientY;
    return Math.hypot(dx, dy);
  };

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      lastTouchDistRef.current = getDistance(e.touches);
      lastZoomRef.current = zoom;
    }
  }, [zoom]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastTouchDistRef.current !== null) {
      e.preventDefault();
      const newDist = getDistance(e.touches);
      const scale = newDist / lastTouchDistRef.current;
      const newZoom = Math.min(maxZoom, Math.max(minZoom, lastZoomRef.current * scale));
      setZoom(newZoom);
    }
  }, [minZoom, maxZoom]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      lastTouchDistRef.current = null;
    }
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
    lastZoomRef.current = 1;
  }, []);

  const zoomIn = useCallback(() => {
    setZoom(prev => {
      const next = Math.min(maxZoom, prev + 0.25);
      lastZoomRef.current = next;
      return next;
    });
  }, [maxZoom]);

  const zoomOut = useCallback(() => {
    setZoom(prev => {
      const next = Math.max(minZoom, prev - 0.25);
      lastZoomRef.current = next;
      return next;
    });
  }, [minZoom]);

  return {
    zoom,
    setZoom,
    resetZoom,
    zoomIn,
    zoomOut,
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
  };
}
